import { formatWhatsApp } from "@baze/schema";
import type { RenderContext, SectionProps } from "../context";
import { messageVars } from "../context";
import { mailHref, socialLinks, telHref, waLink } from "../helpers";
import { Icon, type IconName } from "../icons";
import { BUTTON, Container, EditorHint, MultiLine, REVEAL, STACK, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type ContactNode = SectionProps<"contact">["node"];

interface Method {
  key: string;
  icon: IconName;
  label: string;
  value: string;
  href: string;
  whatsapp?: boolean;
}

function methods(ctx: RenderContext, node: ContactNode, includeWhatsapp: boolean): Method[] {
  const c = node.content;
  const { whatsapp, phone, email } = ctx.site.contact;
  const out: Method[] = [];
  const wa = includeWhatsapp && c.showWhatsapp !== false ? waLink(whatsapp, c.message ?? ctx.site.whatsapp?.defaultMessage, messageVars(ctx)) : null;
  if (wa && whatsapp) out.push({ key: "wa", icon: "whatsapp", label: "WhatsApp", value: formatWhatsApp(whatsapp), href: wa, whatsapp: true });
  const tel = c.showPhone !== false ? telHref(phone) : null;
  if (tel && phone) out.push({ key: "tel", icon: "phone", label: "Telepon", value: phone, href: tel });
  const mail = c.showEmail !== false ? mailHref(email) : null;
  if (mail && email) out.push({ key: "mail", icon: "mail", label: "Email", value: email, href: mail });
  if (c.showSocial !== false) {
    for (const s of socialLinks(ctx.site)) out.push({ key: s.kind, icon: s.kind, label: s.label, value: `@${s.handle}`, href: s.href });
  }
  return out;
}

function waHref(ctx: RenderContext, node: ContactNode) {
  if (node.content.showWhatsapp === false) return null;
  return waLink(ctx.site.contact.whatsapp, node.content.message ?? ctx.site.whatsapp?.defaultMessage, messageVars(ctx));
}

function MethodList({ ctx, list, className }: { ctx: RenderContext; list: Method[]; className?: string }) {
  if (list.length === 0) return null;
  return (
    <ul className={cx("grid gap-3", className)}>
      {list.map((m) => (
        <li key={m.key}>
          <SiteLink
            ctx={ctx}
            href={m.href}
            whatsapp={m.whatsapp}
            className="group flex min-h-16 items-center gap-4 rounded-site-md border border-site-border bg-site-surface px-4 py-3 transition hover:border-site-primary/50 hover:shadow-site-sm"
          >
            <span aria-hidden="true" className="grid size-11 shrink-0 place-items-center rounded-full bg-site-primary/10 text-site-primary transition group-hover:bg-site-primary group-hover:text-site-on-primary">
              <Icon name={m.icon} className="size-5" />
            </span>
            <span className="flex min-w-0 flex-col text-left">
              <span className="text-xs font-medium tracking-wide text-site-muted uppercase">{m.label}</span>
              <span className="truncate font-semibold text-site-text">{m.value}</span>
            </span>
            <Icon name="arrow-right" className="ml-auto size-4 shrink-0 text-site-muted transition group-hover:translate-x-0.5 group-hover:text-site-primary" />
          </SiteLink>
        </li>
      ))}
    </ul>
  );
}

export function Contact({ node, ctx }: SectionProps<"contact">) {
  const c = node.content;
  const wa = waHref(ctx, node);
  const hint = <EditorHint ctx={ctx}>Nomor WhatsApp, telepon, atau email belum diisi di Info bisnis.</EditorHint>;

  if (node.variant === "split") {
    const list = methods(ctx, node, true);
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <div className={cx(REVEAL, "grid gap-10 @4xl:grid-cols-2 @4xl:gap-16")}>
            <div>
              <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
              {wa ? (
                <SiteLink ctx={ctx} href={wa} whatsapp className={cx(BUTTON.primary, "mt-8")}>
                  <Icon name="whatsapp" className="size-[1.125rem]" />
                  Chat via WhatsApp
                </SiteLink>
              ) : null}
            </div>
            {list.length > 0 ? <MethodList ctx={ctx} list={list} className="@4xl:self-center" /> : hint}
          </div>
        </Container>
      </SectionShell>
    );
  }

  const others = methods(ctx, node, false);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size="narrow">
        <div className="baze-reveal-pop relative overflow-hidden rounded-site-lg border border-site-border bg-site-surface px-6 py-10 text-center shadow-site-lg @3xl:px-14 @3xl:py-14">
          <div aria-hidden="true" className="absolute -top-24 left-1/2 size-64 -translate-x-1/2 rounded-full bg-site-primary/10 blur-3xl" />
          <div className="relative">
            <span aria-hidden="true" className="mx-auto mb-6 grid size-16 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md">
              <Icon name={wa ? "whatsapp" : "phone"} className="size-8" />
            </span>
            <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
            {wa && ctx.site.contact.whatsapp ? (
              <div className="mt-8 flex flex-col items-center gap-3">
                <SiteLink ctx={ctx} href={wa} whatsapp className={cx(BUTTON.primary, "w-full text-base @sm:w-auto @sm:px-8")}>
                  <Icon name="whatsapp" className="size-5" />
                  Chat via WhatsApp
                </SiteLink>
                <span className="text-sm text-site-muted tabular-nums">{formatWhatsApp(ctx.site.contact.whatsapp)}</span>
              </div>
            ) : null}
            {c.message && ctx.editor ? (
              <MultiLine ctx={ctx} text={`Pesan otomatis: ${c.message}`} path="content.message" className="mx-auto mt-3 max-w-md text-xs text-site-muted" />
            ) : null}
            {others.length > 0 ? (
              <MethodList ctx={ctx} list={others} className={cx(STACK, "text-left @xl:grid-cols-2")} />
            ) : !wa ? (
              <div className="mt-8">{hint}</div>
            ) : null}
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

